import { formatDate } from '@angular/common';
import { Pagination } from '../../../../models/pagination.model';
import { CourseDetails } from './course-details.model';
import { CourseItem } from './course-item';
import { CourseInstallmentOptions } from './course-settings.model';

export interface CourseResponseDTO extends Pagination {
  docs: CourseItem[];
}

export class CourseDTO implements ICourseDTO {
  title: string;
  targetAudience?: string;
  subject?: string;
  settings: ICourseSettings;
  details: CourseDetails;

  constructor(formValue: CourseFormEntity) {
    this.title = formValue.basic.title;

    const audience =
      formValue.information.targetAudience === 'Other' &&
      formValue.information.targetAudienceOther
        ? formValue.information.targetAudienceOther
        : formValue.information.targetAudience;
    this.targetAudience = audience;

    const subject =
      formValue.information.subject === 'Other' &&
      formValue.information.subjectOther
        ? formValue.information.subjectOther
        : formValue.information.subject;
    this.subject = subject;

    this.settings = {
      maxStudents: formValue.settings.maxStudents,
      isPublic: formValue.settings.isPublic,
      price: formValue.settings.price,
      bannerImageResource: formValue.settings.banner,
      installmentOptions: formValue.settings.installmentOptions,
    };

    this.details = {
      description: formValue.basic.description,
      descriptionSummary: formValue.basic.summary,
      startDate: new Date(formValue.information.startDate),
      medium: formValue.information.medium.toUpperCase(),
      requirements: (formValue.information.requirements || []).filter(
        (item) => !!item?.trim()
      ),
      coverage: (formValue.information.coverage || []).filter(
        (item) => !!item?.trim()
      ),
      totalDurationHour: formValue.information.totalDurationHour || 0,
      totalDurationMinutes: formValue.information.totalDurationMinutes || 0,
    };
  }
}

export interface ICourseDTO {
  title: string;
  targetAudience?: string;
  subject?: string;
  settings: ICourseSettings;
  details: CourseDetails;
}

export interface ICourseContentDTO {
  topic: string;
  resources: [
    {
      title: string;
      resource: string;
    }
  ];
}

export interface ICourseSettings {
  maxStudents: number;
  isPublic: boolean;
  price: number;
  bannerImageResource: string;
  installmentOptions?: CourseInstallmentOptions;
}

export interface CourseFormEntity {
  basic: {
    title: string;
    description: string;
    summary: string;
  };

  information: {
    startDate: string;
    medium: string;
    targetAudience: string;
    targetAudienceOther: string;
    subject: string;
    subjectOther: string;
    requirements: string[];
    coverage: string[];
    totalDurationHour: number;
    totalDurationMinutes: number;
    tags: string;
  };

  settings: {
    maxStudents: number;
    isPublic: boolean;
    price: number;
    banner: string;
    installmentOptions?: CourseInstallmentOptions;
  };
}

export class courseEntity implements CourseFormEntity {
  basic: { title: string; description: string; summary: string };
  information: {
    startDate: string;
    medium: string;
    targetAudience: string;
    targetAudienceOther: string;
    subject: string;
    subjectOther: string;
    requirements: string[];
    coverage: string[];
    totalDurationHour: number;
    totalDurationMinutes: number;
    tags: string;
  };
  settings: {
    maxStudents: number;
    isPublic: boolean;
    price: number;
    banner: string;
    installmentOptions?: CourseInstallmentOptions;
  };

  constructor(courseItem: CourseItem) {
    this.basic = {
      title: courseItem.title,
      description: courseItem.details?.description || '',
      summary: courseItem.details?.descriptionSummary || '',
    };

    this.information = {
      startDate: courseItem.details?.startDate
        ? formatDate(courseItem.details.startDate, 'yyyy-MM-dd', 'en')
        : '',
      medium: courseItem.details?.medium || 'ENGLISH',
      targetAudience: courseItem.targetAudience || '',
      targetAudienceOther: '',
      subject: courseItem.subject || '',
      subjectOther: '',
      requirements: courseItem.details?.requirements || [],
      coverage: courseItem.details?.coverage || [],
      totalDurationHour: courseItem.details?.totalDurationHour || 0,
      totalDurationMinutes: courseItem.details?.totalDurationMinutes || 0,
      tags: '',
    };
    this.settings = {
      maxStudents: courseItem.settings?.maxStudents || 0,
      isPublic: courseItem.settings?.isPublic ?? true,
      price: courseItem.settings?.price || 0,
      banner: courseItem.settings?.bannerImageResource?._id || '',
      installmentOptions: courseItem.settings?.installmentOptions,
    };
  }
}
